import {
  AccountInfo,
  IPublicClientApplication,
  SilentRequest
} from '@azure/msal-browser';
import jwtDecode from 'jwt-decode';
import { RouteComponentProps } from 'react-router-dom';
import { Request, Response, signInExt, signInPlugAndPlay, verifySCGUser } from '../../api/auths';
import { AppDispatch } from '../../app/store';
import { ACTIVE_ACCOUNT_KEY } from '../../constants';
import userProfileSlice, {
  UserContextType
} from '../UserProfile/userProfileSlice';
import { loginRequest } from './authConfig';
import { plugAndPlayConfig } from '../../config/plugAndPlay';

export type System = 'azure-scg';

export function getUser() {
  const token = getToken();
  if (token) {
    const { user }: UserContextType = jwtDecode(token);
    return user;
  }
  return null;
}

export function getToken() {
  return localStorage.getItem('token');
}

export function getExp() {
  const token = getToken();
  if (token) {
    const { exp }: UserContextType = jwtDecode(token);
    return exp;
  }
  return null;
}

export function getActiveAccount() {
  return localStorage.getItem(ACTIVE_ACCOUNT_KEY);
}

export function getCoverPic() {
  return localStorage.getItem('coverPic');
}

export function getProfilePic() {
  return localStorage.getItem('profilePic');
}

// Function to handle silent authentication and account sync
export async function silentSyncActiveAccount(
  instance: IPublicClientApplication,
  activeAccountKey: string
) {
  try {
    const accounts = instance.getAllAccounts(); // Get all available accounts
    const account = accounts.find(
      (account) => account.homeAccountId === activeAccountKey
    ); // Find the matching account

    if (account) {
      const silentRequest: SilentRequest = { account, scopes: ['User.Read'] };
      return await instance.acquireTokenSilent(silentRequest); // Silent auth
    } else {
      return await instance.loginPopup(loginRequest); // Prompt login if account not found
    }
  } catch {}
}

// For SCG Login using Azure AD
export function signInRedirect(instance: IPublicClientApplication) {
  instance.loginRedirect(loginRequest);
}

export function autoSignInRedirect(
  instance: IPublicClientApplication,
  activeAccountKey: string
) {
  const system = localStorage.getItem('auto-login');
  if (system === 'azure-scg') {
    silentSyncActiveAccount(instance, activeAccountKey);
    localStorage.removeItem('auto-login');
  }
}

export async function signInSCG({
  accessTokenRequest,
  instance
}: {
  accessTokenRequest: {
    scopes: string[];
    account: AccountInfo;
  };
  instance: IPublicClientApplication;
}): Promise<UserContextType> {
  const accessTokenResponse = await instance.acquireTokenSilent(
    accessTokenRequest
  );

  // Call User Management API for verify access token
  const response = await verifySCGUser(accessTokenResponse.accessToken);
  return decodeResponse(response);
}

export async function signInExternal(request: Request) {
  const response = await signInExt(request);
  return decodeResponse(response);
}

export async function signInPlugAndPlayAuth(request: Request) {
  const response = await signInPlugAndPlay(request);
  return decodeResponse(response);
}

function decodeResponse(response: Response): UserContextType {
  const { token } = response.data;
  const { user, exp }: UserContextType = jwtDecode(token);

  localStorage.setItem('token', token);
  return { user, token, exp };
}

export function forceSignOut(dispatch: AppDispatch, autoSaveSystem?: System) {
  localStorage.removeItem('token');
  dispatch(userProfileSlice.actions.clear());
  if (autoSaveSystem) saveAutoLoginSystem(autoSaveSystem);
}

export function signOut({
  dispatch,
  instance
}: {
  dispatch: AppDispatch;
  instance: IPublicClientApplication;
}) {
  const user = getUser();
  const activeAccountKey = getActiveAccount();

  if (user) {
    const { isExternalUser } = user;
    localStorage.removeItem('token');
    localStorage.removeItem('coverPic');
    dispatch(userProfileSlice.actions.clear());

    // Check user type and mode before logout
    if (plugAndPlayConfig.isEnabled) {
      // Plug and Play mode: simple logout, redirect to login
      window.location.href = '/login';
    } else if (!isExternalUser) {
      // Legacy mode - Logout for SCG Azure user
      const accounts = instance.getAllAccounts(); // Get all available accounts
      const account = accounts.find(
        (account) => account.homeAccountId === activeAccountKey
      ); // Find the matching account

      if (activeAccountKey) localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
      instance.setActiveAccount(null);
      instance.logoutRedirect({ account });
    } else {
      // Legacy mode - Logout for external user
      window.location.href = `${process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI}`;
    }
  }
}

export function saveAutoLoginSystem(system: System): void {
  localStorage.setItem('auto-login', system);
}

export function saveRedirectUrl(redirectUrl: string): void {
  localStorage.setItem('redirectUrl-space-document', redirectUrl);
}

export function redirectAfterLogin(
  history: RouteComponentProps['history']
): void {
  const redirectUrl = localStorage.getItem('redirectUrl-space-document');

  if (redirectUrl) {
    history.push(redirectUrl);
    localStorage.removeItem('redirectUrl-space-document');
  }
}
